import { createAsyncThunk } from "@reduxjs/toolkit";
import { enrollmentTerminal } from "../../../services/terminal/enrollmentTerminalService";
import { processFile, sendData } from "../../../utils/excelValidatorAndSend";
import {
  createUserInKeycloak,
  checkUserExists,
} from "../../../services/userService";
import { getTerminalDetails } from "../../../services/terminal/terminalService";

// Create enrollment (original functionality)
export const createEnrollment = createAsyncThunk(
  "merchantAndTerminals/createEnrollment",
  async (
    { formData, token, acquirerData, userInfo, onSuccessCallback = false },
    { rejectWithValue }
  ) => {
    try {
      // to local testing, don't push to server
      // const url = "/terminal-enrollment";
      // to server
      const url = "/enrollment";
      const email = formData.acquirer.email;
      const sanitizedUsername = `${acquirerData.acquirerId}_merchant_${formData.acquirer.name
        .toLowerCase()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "")}`;

      // Check if user already exists
      const existingUser = await checkUserExists({
        username: sanitizedUsername,
        email,
        token,
        acquirerData,
      });

      if (existingUser) {
        throw new Error(
          "User already exists with this email or Merchant Name!"
        );
      }

      // Create merchant terminal
      const response = await enrollmentTerminal(
        url,
        formData,
        token,
        acquirerData
      );

      if (response.data) {
        // Create user
        const rol_data =
          formData.acquirer.iso === true || formData.acquirer.iso === "True"
            ? "ISO"
            : "merchant";
        const userData = {
          username: sanitizedUsername,
          email: email,
          emailVerified: false,
          enabled: true,
          credentials: [
            {
              type: "password",
              value: `${acquirerData.acquirerId}merchant123*`,
              temporary: true,
            },
          ],
          attributes: {
            "acquirer-rol-data": 'merchant',
            "acquirer-id-data": acquirerData.acquirerId,
            "acquirer-name-data": acquirerData.acquirerName,
            "acquirer-currency-data": formData.acquirer.currency,
            "acquirer-id-merchant-data": formData.acquirer.merchantID,
            ...(rol_data === "ISO"
              ? { "acquirer-id-iso-data": formData.acquirer.isoID }
              : {}),
          },
        };
        // TODO: is commented due to send email error and rollback fail to
        try {
          await createUserInKeycloak(userData, token, acquirerData);
        } catch (keycloakError) {
          console.error("Error creating user in Keycloak:", keycloakError);
          throw new Error(
            `ISO created, ISO ID: ${response.data.isoID}, but user not created because there is already a user with that email or name!`
          );
        }
      }

      if (onSuccessCallback) {
        onSuccessCallback();
      }

      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error creating enrollment",
      });
    }
  }
);

// Update enrollment (for edit mode)
export const updateEnrollment = createAsyncThunk(
  "merchantAndTerminals/updateEnrollment",
  async (
    { formData, token, acquirerData, enrollmentId },
    { rejectWithValue }
  ) => {
    try {
      const url = `/enrollment/${enrollmentId}`;
      const response = await enrollmentTerminal(
        url,
        formData,
        token,
        acquirerData
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error updating enrollment",
      });
    }
  }
);

// Get enrollment by ID
export const getEnrollmentById = createAsyncThunk(
  "merchantAndTerminals/getEnrollmentById",
  async ({ token, id, acquirerData }, { rejectWithValue }) => {
    try {
      const url = `/enrollment/${id}`;
      const response = await enrollmentTerminal(
        url,
        null,
        token,
        acquirerData,
        "GET"
      );
      return { enrollmentData: response.data };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error retrieving enrollment",
      });
    }
  }
);

// Create enrollment load (from LoadEnrollment.thunk.js)
export const createEnrollmentLoad = createAsyncThunk(
  "merchantAndTerminals/createEnrollmentLoad",
  async (
    { token, formData, userMail, currentStep, enrollment_type },
    { rejectWithValue }
  ) => {
    try {
      const url = "/enrollment-load";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: formData,
          user_mail: userMail,
          current_step: currentStep,
          enrollment_type: enrollment_type,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Error creating enrollment load");
      }

      const enrollmentCreated = await response.json();
      return { enrollmentCreated };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error creating enrollment load",
      });
    }
  }
);

// Update enrollment load (from LoadEnrollment.thunk.js)
export const updateEnrollmentLoad = createAsyncThunk(
  "merchantAndTerminals/updateEnrollmentLoad",
  async (
    {
      token,
      formData,
      userMail,
      enrollmentId,
      currentStep,
      enrollment_completed,
      enrollment_type,
    },
    { rejectWithValue }
  ) => {
    try {
      const url = `/enrollment-load/${enrollmentId}`;
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: formData,
          user_mail: userMail,
          current_step: currentStep,
          enrollment_completed: enrollment_completed || false,
          enrollment_type: enrollment_type,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Error updating enrollment load");
      }

      const enrollmentUpdated = await response.json();
      return { enrollmentUpdated };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error updating enrollment load",
      });
    }
  }
);

// Get enrollment by ID load (from LoadEnrollment.thunk.js)
export const getEnrollmentByIdLoad = createAsyncThunk(
  "merchantAndTerminals/getEnrollmentByIdLoad",
  async ({ token, id }, { rejectWithValue }) => {
    try {
      const url = `/enrollment-load/${id}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Error retrieving enrollment load");
      }

      const enrollmentFilterdById = await response.json();
      return { enrollmentFilterdById };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error retrieving enrollment load",
      });
    }
  }
);

// Process file upload
export const processFileUpload = createAsyncThunk(
  "merchantAndTerminals/processFileUpload",
  async ({ file, token, acquirerData }, { rejectWithValue }) => {
    try {
      const url = "/enrollment";
      // const url = "/terminal-enrollment";

      if (!file) {
        throw new Error("No files selected for upload.");
      }

      const { filteredCellData } = await processFile(file, acquirerData);

      if (!filteredCellData || filteredCellData.length === 0) {
        throw new Error("Processed file is empty or invalid.");
      }

      const { successfulData, failedData } = await sendData(
        filteredCellData,
        url,
        token,
        acquirerData
      );

      return { successfulData, failedData };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error processing file upload",
        error: error.data,
      });
    }
  }
);

// Get Terminal by ID
export const getTerminalById = createAsyncThunk(
  "merchantAndTerminals/getTerminalById",
  async (
    { token, merchantID, acquirerData, terminalID },
    { rejectWithValue }
  ) => {
    try {
      const response = await getTerminalDetails(
        terminalID,
        token,
        acquirerData,
        merchantID
      );
      return { terminalData: response };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Error retrieving terminal",
      });
    }
  }
);

// Get Terminal by ID
export const updateTerminal = createAsyncThunk(
  "merchantAndTerminals/updateTerminal",
  async (
    { formData, token, acquirerData, onSuccessCallback = false },
    { rejectWithValue }
  ) => {
    try {
      const url = "/edit";
      const response = await enrollmentTerminal(
        url,
        formData,
        token,
        acquirerData
      );
      if (onSuccessCallback) {
        onSuccessCallback();
      }
      return { terminalData2: response.data };
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        "Error updating terminal";
      return rejectWithValue({
        message: errorMessage,
      });
    }
  }
);
